import React, { useEffect, useRef, useState } from "react";

export function OutputFigure({ src, alt, caption, href }) {
  const img = <img src={src} alt={alt} />;
  return (
    <figure className="nb-fig">
      {href ? (
        <a href={href} target="_blank" rel="noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}

function CellTools({ editing, onRun, onToggleEdit }) {
  return (
    <div className="cell-tools">
      <button type="button" className="cell-tool" onClick={onRun}>
        Run
      </button>
      <button type="button" className="cell-tool" onClick={onToggleEdit}>
        {editing ? "Save" : "Edit"}
      </button>
    </div>
  );
}

export function MarkdownCell({
  id,
  selected,
  onSelect,
  running,
  onRun,
  source,
  onSource,
  children,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(source || "");
  const areaRef = useRef(null);

  useEffect(() => {
    setDraft(source || "");
  }, [source]);

  useEffect(() => {
    if (editing && areaRef.current) {
      areaRef.current.focus();
      areaRef.current.setSelectionRange(
        areaRef.current.value.length,
        areaRef.current.value.length
      );
    }
  }, [editing]);

  const save = () => {
    setEditing(false);
    if (onSource) onSource(draft);
  };

  return (
    <article
      id={id}
      className={`cell cell-md${selected ? " is-selected" : ""}${
        running ? " is-running" : ""
      }`}
      onClick={onSelect}
      onDoubleClick={() => setEditing(true)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.shiftKey) {
          e.preventDefault();
          save();
          onRun?.();
        } else if (e.key === "Escape") {
          setEditing(false);
          setDraft(source || "");
        } else if (
          e.key === "Enter" &&
          selected &&
          !editing &&
          e.target === e.currentTarget
        ) {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      {selected ? (
        <CellTools
          editing={editing}
          onRun={(e) => {
            e.stopPropagation();
            save();
            onRun?.();
          }}
          onToggleEdit={(e) => {
            e.stopPropagation();
            if (editing) save();
            else setEditing(true);
          }}
        />
      ) : null}
      <div className="cell-gutter" aria-hidden="true" />
      <div className="cell-body md">
        {editing ? (
          <textarea
            ref={areaRef}
            className="cell-edit cell-edit-md"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={save}
            rows={Math.max(4, draft.split("\n").length + 1)}
            aria-label="Edit markdown cell"
          />
        ) : source && draft !== source ? (
          <p className="md-draft">{draft}</p>
        ) : (
          children
        )}
      </div>
    </article>
  );
}

export function CodeCell({
  id,
  n,
  selected,
  onSelect,
  code,
  source,
  onSource,
  running,
  onRun,
  children,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(source || "");
  const areaRef = useRef(null);
  const prompt = running ? "In [*]" : `In [${n}]`;

  useEffect(() => {
    setDraft(source || "");
  }, [source]);

  useEffect(() => {
    if (editing && areaRef.current) areaRef.current.focus();
  }, [editing]);

  const save = () => {
    setEditing(false);
    if (onSource) onSource(draft);
  };

  return (
    <article
      id={id}
      className={`cell cell-code${selected ? " is-selected" : ""}${
        running ? " is-running" : ""
      }`}
      onClick={onSelect}
      onDoubleClick={() => setEditing(true)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.shiftKey) {
          e.preventDefault();
          save();
          onRun?.();
        } else if (e.key === "Escape") {
          setEditing(false);
          setDraft(source || "");
        } else if (
          e.key === "Enter" &&
          selected &&
          !editing &&
          e.target === e.currentTarget
        ) {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      {selected ? (
        <CellTools
          editing={editing}
          onRun={(e) => {
            e.stopPropagation();
            save();
            onRun?.();
          }}
          onToggleEdit={(e) => {
            e.stopPropagation();
            if (editing) save();
            else setEditing(true);
          }}
        />
      ) : null}
      <div className="cell-run">
        <div className="cell-gutter">
          <span className="prompt prompt-in">{prompt}:</span>
        </div>
        {editing ? (
          <textarea
            ref={areaRef}
            className="cell-edit"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={save}
            rows={Math.max(3, draft.split("\n").length + 1)}
            spellCheck={false}
            aria-label={`Edit code cell ${n}`}
          />
        ) : (
          <pre className="cell-src">
            <code>{code || draft}</code>
          </pre>
        )}
      </div>
      {children ? (
        <div className="cell-run cell-run-out">
          <div className="cell-gutter">
            <span className="prompt prompt-out">
              {running ? "Out[*]" : `Out[${n}]`}:
            </span>
          </div>
          <div className="cell-out-body">{children}</div>
        </div>
      ) : null}
    </article>
  );
}

export function Kw({ children }) {
  return <span className="tok-kw">{children}</span>;
}
export function Str({ children }) {
  return <span className="tok-str">{children}</span>;
}
export function Cmt({ children }) {
  return <span className="tok-cmt">{children}</span>;
}
